export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;
export const TAU = Math.PI * 2;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function inverseLerp(a: number, b: number, value: number): number {
  return a === b ? 0 : (value - a) / (b - a);
}

/** Smooth 0 → 1 ramp (cubic Hermite) of t, clamped – starts and ends without a jolt. */
export function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

/**
 * Frame-rate independent exponential smoothing.
 * `rate` is in 1/s: after 1/rate seconds ~63 % of the distance is covered.
 */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

/** Velocity state for smoothDamp (kept by the caller between frames). */
export interface SmoothDampState {
  velocity: number;
}

/**
 * Critically damped spring towards a target (a.k.a. SmoothDamp): starts
 * gently, never overshoots and has continuous velocity – use it for camera
 * moves that must not feel like a jump. smoothTime ≈ time to reach the target.
 */
export function smoothDamp(current: number, target: number, state: SmoothDampState, smoothTime: number, dt: number): number {
  if (dt <= 0) return current;
  const omega = 2 / Math.max(1e-4, smoothTime);
  const x = omega * dt;
  const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  const change = current - target;
  const temp = (state.velocity + omega * change) * dt;
  state.velocity = (state.velocity - omega * temp) * decay;
  let output = target + (change + temp) * decay;
  // Prevent overshooting.
  if (target - current > 0 === output > target) {
    output = target;
    state.velocity = 0;
  }
  return output;
}

/** Wraps an angle to (-π, π]. */
export function wrapAngle(angle: number): number {
  let a = angle % TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}

/** Exponential smoothing for angles – always takes the short way around. */
export function dampAngle(current: number, target: number, rate: number, dt: number): number {
  return current + wrapAngle(target - current) * (1 - Math.exp(-rate * dt));
}

/** Snaps tiny floating point noise away (e.g. 1.0000000002 → 1). */
export function snap(value: number, epsilon = 1e-9): number {
  const rounded = Math.round(value);
  return Math.abs(value - rounded) < epsilon ? rounded : value;
}
